import { useState, useEffect } from "react";
import { modulesAPI, questionsAPI } from "../../services/api";
import {
  Box, Typography, Button, Card, CardContent, Chip, Accordion,
  AccordionSummary, AccordionDetails, TextField, Select, MenuItem,
  FormControl, InputLabel, FormControlLabel, Checkbox, Alert,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Grid
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import QuizIcon from "@mui/icons-material/Quiz";

export default function QuestionBank() {
  const [paths, setPaths] = useState([]);
  const [selectedPath, setSelectedPath] = useState("");
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState("");
  const [questions, setQuestions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [allModules, setAllModules] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [open, setOpen] = useState(false);
  const [topicOpen, setTopicOpen] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editQuestion, setEditQuestion] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [topicForm, setTopicForm] = useState({ topic_name: "", description: "", is_mandatory: false });
  const [templateForm, setTemplateForm] = useState({
    template_name: "", assessment_type: "mock", question_count: 10,
    time_limit_minutes: 30, pass_mark: 80, randomise_questions: true,
    randomise_answers: true, feedback_mode: "score_and_topics",
    retry_allowed: true, max_attempts: 3, cooldown_hours: 24
  });
  const [qForm, setQForm] = useState({
    question_text: "", question_type: "multiple_choice",
    difficulty: "medium", explanation: "", is_critical: false,
    image_url: "", topic_id: "",
    answer_options: [
      { option_text: "", is_correct: false, display_order: 1 },
      { option_text: "", is_correct: false, display_order: 2 },
      { option_text: "", is_correct: false, display_order: 3 },
      { option_text: "", is_correct: false, display_order: 4 },
    ]
  });

  useEffect(() => { loadPaths(); loadAllModules(); }, []);

  useEffect(() => {
    if (selectedPath) {
      loadModules(selectedPath);
    } else {
      setModules(allModules);
      // Reload all questions
      if (allModules.length > 0) {
        setLoading(true);
        const loadAll = async () => {
          const allQ = [];
          for (const m of allModules) {
            try {
              const qRes = await questionsAPI.getQuestions(m.module_id);
              allQ.push(...qRes.data);
            } catch {}
          }
          setQuestions(allQ);
          setLoading(false);
        };
        loadAll();
      }
    }
  }, [selectedPath]);

  const loadPaths = async () => {
    const res = await modulesAPI.getAllPaths();
    setPaths(res.data);
  };

  const loadAllModules = async () => {
    try {
      const res = await modulesAPI.getModules();
      setAllModules(res.data);
      setModules(res.data);
      // Load all questions
      setLoading(true);
      const allQ = [];
      for (const m of res.data) {
        try {
          const qRes = await questionsAPI.getQuestions(m.module_id);
          allQ.push(...qRes.data);
        } catch {}
      }
      setQuestions(allQ);
      setLoading(false);
    } catch { setLoading(false); }
  };

  const loadModules = async (pathId) => {
    const res = await modulesAPI.getModules(pathId);
    setModules(res.data);
    setSelectedModule("");
    loadQuestions("all", res.data);
  };

  const loadQuestions = async (moduleId, moduleList) => {
    const mod = moduleId !== undefined ? moduleId : selectedModule;
    const mods = moduleList !== undefined ? moduleList : modules;
    setLoading(true);
    try {
      if (mod === "all") {
        const allQ = [];
        for (const m of mods) {
          try {
            const res = await questionsAPI.getQuestions(m.module_id);
            allQ.push(...res.data);
          } catch {}
        }
        setQuestions(allQ);
      } else if (mod) {
        const res = await questionsAPI.getQuestions(mod);
        setQuestions(res.data);
      }
    } catch {} finally { setLoading(false); }
  };

  const loadTopics = async (moduleId) => {
    const mod = moduleId || selectedModule;
    if (!mod || mod === "all") { setTopics([]); return; }
    try {
      const res = await questionsAPI.getTopics(mod);
      setTopics(res.data);
    } catch {}
  };

  const loadTemplates = async (moduleId) => {
    if (!moduleId || moduleId === "all") { setTemplates([]); return; }
    try {
      const res = await questionsAPI.getTemplates(moduleId);
      setTemplates(res.data);
    } catch {}
  };

  const handleCreateTopic = async () => {
    if (!topicForm.topic_name.trim()) { setError("Topic name is required."); return; }
    try {
      await questionsAPI.createTopic({ ...topicForm, module_id: selectedModule });
      setSuccess("Topic created.");
      setTopicOpen(false);
      setTopicForm({ topic_name: "", description: "", is_mandatory: false });
      loadTopics();
    } catch (err) { const detail = err.response?.data?.detail; setError(typeof detail === 'string' ? detail : "Failed to create template."); }
  };

  const handleCreateQuestion = async () => {
    try {
      const payload = {
        ...qForm,
        module_id: selectedModule,
        topic_id: qForm.topic_id || null,
        answer_options: qForm.answer_options.filter(o => o.option_text.trim() !== "")
      };
      await questionsAPI.createQuestion(payload);
      setSuccess("Question added to the bank.");
      setOpen(false);
      loadQuestions();
    } catch (err) { setError(err.response?.data?.detail || "Failed to create question."); }
  };

  const handleCreateTemplate = async () => {
    try {
      let moduleId = selectedModule;
      if (selectedModule === "all") {
        if (templateForm.assessment_type !== "final") {
          setError("Please select a specific module for non-final templates.");
          return;
        }
        // Find the Final Exam module in this path
        const finalMod = modules.find(m => m.module_title === "Final Exam");
        if (!finalMod) {
          setError("No Final Exam module found in this path. Please contact your administrator.");
          return;
        }
        moduleId = finalMod.module_id;
      }
      await questionsAPI.createTemplate({ ...templateForm, module_id: moduleId });
      setSuccess("Assessment template created.");
      setTemplateOpen(false);
      loadTemplates(selectedModule);
    } catch (err) { const detail = err.response?.data?.detail; setError(typeof detail === 'string' ? detail : "Failed to create template."); }
  };

  const handleEditTemplate = (t) => {
    setEditTemplate(t);
    setTemplateForm({
      template_name: t.template_name,
      assessment_type: t.assessment_type,
      question_count: t.question_count,
      time_limit_minutes: t.time_limit_minutes,
      pass_mark: t.pass_mark,
      max_attempts: t.max_attempts,
      cooldown_hours: t.cooldown_hours,
      randomise_questions: t.randomise_questions || false,
      show_feedback: t.show_feedback || false,
      feedback_mode: t.feedback_mode || "score_only",
      retry_allowed: t.retry_allowed || true,
    });
    setTemplateOpen(true);
  };

  const handleUpdateTemplate = async () => {
    try {
      await questionsAPI.updateTemplate(editTemplate.template_id, {...templateForm, module_id: selectedModule});
      setSuccess("Template updated successfully.");
      setTemplateOpen(false);
      setEditTemplate(null);
      loadTemplates(selectedModule);
    } catch (err) {
      const detail = err.response?.data?.detail;
      console.error("Update template error:", err.response?.data);
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail) || "Failed to update template.");
    }
  };

  const updateOption = (index, field, value) => {
    const updated = [...qForm.answer_options];
    updated[index] = { ...updated[index], [field]: value };
    setQForm({ ...qForm, answer_options: updated });
  };

  const handleEditOpen = (q) => {
    setEditQuestion(q);
    setEditForm({
      question_text: q.question_text,
      question_type: q.question_type,
      difficulty: q.difficulty,
      explanation: q.explanation || "",
      is_critical: q.is_critical,
      topic_id: q.topic_id || "",
      module_id: selectedModule,
      answer_options: q.answer_options.map((o, i) => ({
        option_text: o.option_text,
        is_correct: o.is_correct,
        display_order: i + 1
      }))
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      await questionsAPI.updateQuestion(editQuestion.question_id, {
        ...editForm,
        topic_id: editForm.topic_id || null,
        answer_options: editForm.answer_options.filter(o => o.option_text.trim() !== "")
      });
      setSuccess("Question updated.");
      setEditOpen(false);
      loadQuestions();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to update question.");
    }
  };

  const updateEditOption = (index, field, value) => {
    const updated = [...editForm.answer_options];
    updated[index] = { ...updated[index], [field]: value };
    setEditForm({ ...editForm, answer_options: updated });
  };

  const diffColor = { easy: "success", medium: "warning", hard: "error" };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1a237e">Question Bank</Typography>
          <Typography variant="body2" color="text.secondary">Manage exam questions and templates</Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={() => setTopicOpen(true)} disabled={!selectedModule || selectedModule === "all"}>
            + Topic
          </Button>
          <Button variant="outlined" onClick={() => {
            const isFinalModule = modules.find(m => m.module_id === selectedModule)?.module_title === "Final Exam";
            setTemplateForm(f => ({...f, assessment_type: isFinalModule ? "final" : "mock"}));
            setTemplateOpen(true);
          }} disabled={!selectedModule}>
            + Template
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
            setQForm({
              question_text: "", question_type: "multiple_choice", difficulty: "medium",
              explanation: "", is_critical: false, image_url: "", topic_id: "",
              answer_options: [
                { option_text: "", is_correct: false, display_order: 1 },
                { option_text: "", is_correct: false, display_order: 2 },
                { option_text: "", is_correct: false, display_order: 3 },
                { option_text: "", is_correct: false, display_order: 4 },
              ]
            });
            setOpen(true);
          }}
            disabled={!selectedModule}
            sx={{ background: selectedModule ? "linear-gradient(135deg, #1a237e, #0d47a1)" : undefined }}>
            Add Question
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel>Learning Path</InputLabel>
          <Select value={selectedPath} label="Learning Path"
            onChange={e => setSelectedPath(e.target.value)}>
            {paths.map(p => <MenuItem key={p.path_id} value={p.path_id}>{p.path_name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 220 }}>
          <InputLabel>Module</InputLabel>
          <Select value={selectedModule} label="Module"
            onChange={e => {
              setSelectedModule(e.target.value);
              loadQuestions(e.target.value, modules);
              loadTemplates(e.target.value);
              loadTopics(e.target.value);
            }}>
            <MenuItem value="" disabled>— Select a Module —</MenuItem>
              <MenuItem value="all">All Modules</MenuItem>
            {modules.map(m => <MenuItem key={m.module_id} value={m.module_id}>{m.module_title}</MenuItem>)}
          </Select>
        </FormControl>
        {topics.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {topics.map(t => (
              <Chip key={t.topic_id} label={t.topic_name}
                color={t.is_mandatory ? "primary" : "default"} size="small" />
            ))}
          </Box>
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" mb={2}>
        {questions.length} questions {selectedModule === "all" ? "across all modules" : "in this module"}
      </Typography>
      {selectedModule === "all" && (
        <Typography variant="caption" color="warning.main" mb={1} display="block">
          ⚠️ Select a specific module to add questions, topics or templates
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>
      ) : questions.length === 0 ? (
        <Card sx={{ borderRadius: 3, textAlign: "center", p: 4 }}>
          <QuizIcon sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No questions yet</Typography>
        </Card>
      ) : (
        questions.map((q, i) => (
          <Accordion key={q.question_id} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 24 }}>
                  {i + 1}.
                </Typography>
                <Typography variant="body2" fontWeight={500} sx={{ flexGrow: 1 }}>
                  {q.question_text}
                </Typography>
                <Chip label={q.difficulty} color={diffColor[q.difficulty]} size="small" />
                {q.is_critical && <Chip label="Critical" color="error" size="small" />}
                {q.topic && <Chip label={q.topic.topic_name} size="small" variant="outlined" />}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {q.answer_options.map((opt, j) => (
                <Box key={opt.option_id} sx={{
                  p: 1, mb: 0.5, borderRadius: 1,
                  bgcolor: opt.is_correct ? "#e8f5e9" : "#fafafa",
                  border: `1px solid ${opt.is_correct ? "#4caf50" : "#e0e0e0"}`
                }}>
                  <Typography variant="body2">
                    {opt.is_correct ? "✅" : "❌"} {opt.option_text}
                  </Typography>
                </Box>
              ))}
              {q.explanation && (
                <Typography variant="body2" color="text.secondary" mt={1}>
                  <b>Explanation:</b> {q.explanation}
                </Typography>
              )}
              <Box sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" startIcon={<EditIcon />}
                  onClick={() => handleEditOpen(q)}>
                  Edit Question
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* Templates Section */}
      {templates.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" fontWeight={700} color="#1a237e" mb={2}>
            Assessment Templates
          </Typography>
          {templates.map(t => (
            <Card key={t.template_id} variant="outlined" sx={{ mb: 1, borderRadius: 2 }}>
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body1" fontWeight={600}>{t.template_name}</Typography>
                      <Button size="small" variant="outlined" onClick={() => handleEditTemplate(t)} sx={{ ml: 1 }}>Edit</Button>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                      <Chip label={`${t.question_count} questions`} size="small" />
                      <Chip label={`Pass: ${t.pass_mark}%`} size="small" color="success" />
                      <Chip label={`${t.time_limit_minutes} min`} size="small" />
                      <Chip label={`Max ${t.max_attempts} attempts`} size="small" />
                      <Chip label={`${t.cooldown_hours}h cooldown`} size="small" />
                    </Box>
                  </Box>
                  <Chip label={t.is_active ? "Active" : "Inactive"}
                    color={t.is_active ? "success" : "default"} size="small" />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Add Question Dialog */}
      <Dialog open={open} onClose={() => { setOpen(false); }} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1a237e" }}>Add Question</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Question Text" value={qForm.question_text}
            onChange={e => setQForm({...qForm, question_text: e.target.value})}
            multiline rows={2} required sx={{ mt: 2, mb: 2 }} />
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Type</InputLabel>
              <Select value={qForm.question_type} label="Type"
                onChange={e => {
                const newType = e.target.value;
                if (newType === "true_false") {
                  setQForm({...qForm, question_type: newType, answer_options: [
                    { option_text: "True", is_correct: false, display_order: 1 },
                    { option_text: "False", is_correct: false, display_order: 2 },
                  ]});
                } else if (newType === "multiple_choice" || newType === "multiple_select") {
                  setQForm({...qForm, question_type: newType, answer_options: [
                    { option_text: "", is_correct: false, display_order: 1 },
                    { option_text: "", is_correct: false, display_order: 2 },
                    { option_text: "", is_correct: false, display_order: 3 },
                    { option_text: "", is_correct: false, display_order: 4 },
                  ]});
                } else {
                  setQForm({...qForm, question_type: newType});
                }
              }}>
                <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                <MenuItem value="true_false">True / False</MenuItem>
                <MenuItem value="multiple_select">Multiple Select</MenuItem>
                              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Difficulty</InputLabel>
              <Select value={qForm.difficulty} label="Difficulty"
                onChange={e => setQForm({...qForm, difficulty: e.target.value})}>
                <MenuItem value="easy">Easy</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="hard">Hard</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Topic (optional)</InputLabel>
            <Select value={qForm.topic_id} label="Topic (optional)"
              onChange={e => setQForm({...qForm, topic_id: e.target.value})}>
              <MenuItem value="">None</MenuItem>
              {topics.map(t => <MenuItem key={t.topic_id} value={t.topic_id}>{t.topic_name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Checkbox checked={qForm.is_critical}
              onChange={e => setQForm({...qForm, is_critical: e.target.checked})} />}
            label="Critical question (must answer correctly to pass)"
            sx={{ mb: 2 }}
          />
          {/* Image upload */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1}>Question Image (optional)</Typography>
            {qForm.image_url ? (
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <img src={qForm.image_url} alt="Question" style={{ maxHeight: 150, borderRadius: 8, border: "1px solid #e0e0e0" }} />
                <Button size="small" color="error" onClick={() => setQForm({...qForm, image_url: ""})}
                  sx={{ position: "absolute", top: 0, right: 0 }}>✕</Button>
              </Box>
            ) : (
              <Button variant="outlined" component="label" size="small">
                Upload Image
                <input type="file" hidden accept="image/*" onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => setQForm({...qForm, image_url: reader.result});
                  reader.readAsDataURL(file);
                }} />
              </Button>
            )}
          </Box>

          {/* Hide answer options for short_answer type */}
          {qForm.question_type !== "short_answer" && (
            <Box>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>Answer Options</Typography>
              {qForm.answer_options.map((opt, i) => (
                <Box key={i} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
                  <Checkbox checked={opt.is_correct}
                    onChange={e => updateOption(i, "is_correct", e.target.checked)}
                    color="success" />
                  <TextField fullWidth size="small" label={`Option ${i + 1}`}
                    value={opt.option_text}
                    onChange={e => updateOption(i, "option_text", e.target.value)} />
                </Box>
              ))}
              <Typography variant="caption" color="text.secondary">
                ✅ Check the box next to the correct answer(s)
              </Typography>
            </Box>
          )}
          <TextField fullWidth label="Explanation (shown after attempt)" value={qForm.explanation}
            onChange={e => setQForm({...qForm, explanation: e.target.value})}
            multiline rows={2} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateQuestion}
            disabled={!qForm.question_text}
            sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
            Add Question
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Topic Dialog */}
      <Dialog open={topicOpen} onClose={() => setTopicOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1a237e" }}>Create Topic</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Topic Name" value={topicForm.topic_name}
            onChange={e => setTopicForm({...topicForm, topic_name: e.target.value})}
            required sx={{ mt: 2, mb: 2 }} />
          <TextField fullWidth label="Description" value={topicForm.description}
            onChange={e => setTopicForm({...topicForm, description: e.target.value})}
            multiline rows={2} sx={{ mb: 2 }} />
          <FormControlLabel
            control={<Checkbox checked={topicForm.is_mandatory}
              onChange={e => setTopicForm({...topicForm, is_mandatory: e.target.checked})} />}
            label="Mandatory topic (every exam must include at least one question from this topic)" />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setTopicOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateTopic}
            sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
            Create Topic
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Template Dialog */}
      <Dialog open={templateOpen} onClose={() => { setTemplateOpen(false); setEditTemplate(null); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1a237e" }}>{editTemplate ? "Edit Assessment Template" : "Create Assessment Template"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Template Name" value={templateForm.template_name}
                onChange={e => setTemplateForm({...templateForm, template_name: e.target.value})} required />
            </Grid>

            <Grid item xs={6}>
              <TextField fullWidth label="Number of Questions" type="number"
                value={templateForm.question_count}
                onChange={e => setTemplateForm({...templateForm, question_count: parseInt(e.target.value)})} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Pass Mark %" type="number"
                value={templateForm.pass_mark}
                onChange={e => setTemplateForm({...templateForm, pass_mark: parseFloat(e.target.value)})} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Time Limit (minutes)" type="number"
                value={templateForm.time_limit_minutes}
                onChange={e => setTemplateForm({...templateForm, time_limit_minutes: parseInt(e.target.value)})} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Max Attempts" type="number"
                value={templateForm.max_attempts}
                onChange={e => setTemplateForm({...templateForm, max_attempts: parseInt(e.target.value)})} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Cooldown Hours" type="number"
                value={templateForm.cooldown_hours}
                onChange={e => setTemplateForm({...templateForm, cooldown_hours: parseInt(e.target.value)})} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setTemplateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={editTemplate ? handleUpdateTemplate : handleCreateTemplate}
            disabled={!templateForm.template_name}
            sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
            {editTemplate ? "Save Changes" : "Create Template"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#1a237e" }}>Edit Question</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Question Text" value={editForm.question_text || ""}
            onChange={e => setEditForm({...editForm, question_text: e.target.value})}
            multiline rows={2} required sx={{ mt: 2, mb: 2 }} />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={editForm.question_type || "multiple_choice"} label="Type"
                  onChange={e => setEditForm({...editForm, question_type: e.target.value})}>
                  <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                  <MenuItem value="true_false">True / False</MenuItem>
                  <MenuItem value="multiple_select">Multiple Select</MenuItem>
                                  </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Difficulty</InputLabel>
                <Select value={editForm.difficulty || "medium"} label="Difficulty"
                  onChange={e => setEditForm({...editForm, difficulty: e.target.value})}>
                  <MenuItem value="easy">Easy</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="hard">Hard</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <FormControl fullWidth>
                <InputLabel>Topic</InputLabel>
                <Select value={editForm.topic_id || ""} label="Topic"
                  onChange={e => setEditForm({...editForm, topic_id: e.target.value})}>
                  <MenuItem value="">None</MenuItem>
                  {topics.map(t => <MenuItem key={t.topic_id} value={t.topic_id}>{t.topic_name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <FormControlLabel
            control={<Checkbox checked={editForm.is_critical || false}
              onChange={e => setEditForm({...editForm, is_critical: e.target.checked})} />}
            label="Critical question" sx={{ mb: 2 }} />
          <Typography variant="subtitle2" fontWeight={700} mb={1}>Answer Options</Typography>
          {(editForm.answer_options || []).map((opt, i) => (
            <Box key={i} sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
              <Checkbox checked={opt.is_correct}
                onChange={e => updateEditOption(i, "is_correct", e.target.checked)}
                color="success" />
              <TextField fullWidth size="small" label={`Option ${i + 1}`}
                value={opt.option_text}
                onChange={e => updateEditOption(i, "option_text", e.target.value)} />
            </Box>
          ))}
          <TextField fullWidth label="Explanation" value={editForm.explanation || ""}
            onChange={e => setEditForm({...editForm, explanation: e.target.value})}
            multiline rows={2} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveEdit}
            sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
